import { useProcess } from '@fasstech/oip-starter-utils/app';
import { useForm } from 'react-hook-form';
import { CollegeEnum } from '@fasstech/oip-core/entities/enums';
import useSaveSimulationDemographyMutation from './useSaveSimulationDemographyMutation.js';
import {
  always,
  and,
  anyPass,
  compose,
  either,
  evolve,
  filter,
  gt,
  head,
  includes,
  isEmpty,
  isNil,
  path,
  prop,
  propOr,
  toString,
  when
} from 'ramda';

const useSimulationDemographyForm = (getDemography, getMiddleText, getMiddleNumber) => {
  const { processHandlers, context } = useProcess();
  const { onFormSubmit } = useSaveSimulationDemographyMutation();

  const safeToString = (value) => (value == null ? undefined : toString(value));

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      demography: {
        employeeCount: safeToString(path(['demography', 'employeeCount'])(getDemography)),
        employeeAverageAge: safeToString(path(['demography', 'employeeAverageAge'])(getDemography)),
        partnerCount: safeToString(path(['demography', 'partnerCount'])(getDemography)),
        childrenCount: safeToString(path(['demography', 'childrenCount'])(getDemography)),
        college: path(['demography', 'college'], getDemography),
        socialSecurities: [
          {
            regime: path(['demography', 'socialSecurities', 0, 'regime'], getDemography)
          }
        ]
      }
    }
  });

  const $onPrevClick = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const { isValid } = form.formState;

  const collegeOptions = [
    {
      label: 'Ensemble du personnel',
      value: CollegeEnum.ALL
    },
    {
      label: 'Cadres',
      value: CollegeEnum.EXECUTIVE_EMPLOYEES
    },
    {
      label: 'Non cadres',
      value: CollegeEnum.EMPLOYEES
    }
  ];

  const filteredCollegeOptions = filter(
    ({ label }) => includes(label, propOr([], 'colleges', getDemography)),
    collegeOptions
  );

  const minEmployeeQuantity = getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_MIN');
  const maxEmployeeQuantity = getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_MAX');
  const minSpouseQuantity = getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_MIN');
  const maxSpouseQuantity = getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_MAX');
  const minChildrenQuantity = getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_MIN');
  const maxChildrenQuantity = getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_MAX');
  const minEmployeeAverageAge = getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_MIN');
  const maxEmployeeAverageAge = getMiddleNumber('ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_MAX');
  const employeeQuantityInfo = getMiddleText('ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_INFO', {
    minEmployeeQuantity,
    maxEmployeeQuantity
  });
  const spouseQuantityInfo = getMiddleText('ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_INFO', {
    minSpouseQuantity,
    maxSpouseQuantity
  });
  const childrenQuantityInfo = getMiddleText('ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_INFO', {
    minChildrenQuantity,
    maxChildrenQuantity
  });
  const employeeAverageAgeInfo = getMiddleText('ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_INFO', {
    minEmployeeAverageAge,
    maxEmployeeAverageAge
  });
  const tooltipText = (min, max, info) =>
    anyPass([() => and(gt(min, -1), gt(max, -1)), () => gt(max, -1), () => gt(min, -1)])()
      ? info
      : undefined;

  const onSubmit = (values) => {
    // convert to int
    const toInt = (val) => {
      if (val === '' || val === null || val === undefined) {
        return null;
      }
      return parseInt(val, 10);
    };

    const cleanSocialSecurities = when(
      compose(either(isNil, isEmpty), prop('regime'), head),
      always([])
    );

    const formatNumbersToInt = evolve({
      demography: evolve({
        employeeCount: toInt,
        employeeAverageAge: toInt,
        partnerCount: toInt,
        childrenCount: toInt,
        socialSecurities: cleanSocialSecurities
      })
    });
    onFormSubmit(formatNumbersToInt(values), (ok, err) => {
      if (ok && !err) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      }
    });
  };

  return {
    form,
    $onPrevClick,
    isValid,
    collegeOptions: filteredCollegeOptions,
    employeeCountTooltipText: tooltipText(
      minEmployeeQuantity,
      maxEmployeeQuantity,
      employeeQuantityInfo
    ),
    spouseCountTooltipText: tooltipText(minSpouseQuantity, maxSpouseQuantity, spouseQuantityInfo),
    childrenCountTooltipText: tooltipText(
      minChildrenQuantity,
      maxChildrenQuantity,
      childrenQuantityInfo
    ),
    employeeAverageAgeTooltipText: tooltipText(
      minEmployeeAverageAge,
      maxEmployeeAverageAge,
      employeeAverageAgeInfo
    ),
    onSubmit
  };
};

export default useSimulationDemographyForm;
